export const INQUIRY_TYPES = [
  'Product Support',
  'Billing',
  'Privacy Request',
  'Legal Inquiry',
  'Security Issue',
  'Feature Request',
] as const;

export type InquiryType = (typeof INQUIRY_TYPES)[number];

export type ContactFormValues = {
  name: string;
  email: string;
  inquiryType: InquiryType;
  subject: string;
  message: string;
  companyWebsite: string;
};

export type ContactFieldErrors = Partial<Record<keyof ContactFormValues, string>>;

export type ContactResponse = {
  ok: boolean;
  referenceId?: string;
  inquiryType?: InquiryType;
  destinationTeamLabel?: string;
  submitterEmail?: string;
  routedToLabel?: string;
  deliveryWarning?: 'confirmation_email_failed' | string;
  error?: string;
  fieldErrors?: ContactFieldErrors;
};

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export type TurnstileRenderOptions = {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
};

export const TURNSTILE_SCRIPT_ID = 'trade-proof-turnstile-script';

export function isValidEmail(email: string) {
  return /^\S+@\S+\.\S+$/.test(email.trim());
}

export function normalizeText(value: string) {
  return value.replace(/\r\n/g, '\n').split('\u0000').join('').trim();
}

export function validateContactForm(form: ContactFormValues): ContactFieldErrors {
  const errors: ContactFieldErrors = {};

  if (!normalizeText(form.name)) errors.name = 'Name is required.';
  if (!normalizeText(form.email)) errors.email = 'Email is required.';
  else if (!isValidEmail(form.email)) errors.email = 'Enter a valid email address.';
  if (!INQUIRY_TYPES.includes(form.inquiryType)) errors.inquiryType = 'Choose an inquiry type.';
  if (!normalizeText(form.subject)) errors.subject = 'Subject is required.';
  if (!normalizeText(form.message)) errors.message = 'Message is required.';

  return errors;
}

export function getContactSubmitErrorMessage(error?: string | null) {
  if (error === 'validation_error') {
    return 'Please correct the highlighted fields and try again.';
  }
  if (error === 'contact_not_configured') {
    return 'The contact inbox is not configured yet. Please try again later.';
  }
  if (error === 'contact_rate_limited') {
    return 'Too many contact attempts. Please wait a little and try again.';
  }
  if (error === 'contact_send_timeout' || error === 'contact_request_timeout') {
    return 'The support request took too long to send. Please try again.';
  }
  if (error === 'turnstile_required' || error === 'turnstile_failed') {
    return 'Please complete the security check and try again.';
  }
  if (error === 'spam_detected') {
    return 'We could not accept that message. Please try again with a shorter, more direct note.';
  }
  if (error === 'contact_confirmation_failed') {
    return 'Your message was sent, but the confirmation email could not be delivered. Our team still received your request.';
  }

  return 'We could not send your message right now. Please try again in a moment.';
}

export function getHeadlineTypeLabel(type: InquiryType | null) {
  if (!type) return '';

  switch (type) {
    case 'Product Support':
      return 'support request';
    case 'Billing':
      return 'billing request';
    case 'Privacy Request':
      return 'privacy request';
    case 'Legal Inquiry':
      return 'legal inquiry';
    case 'Security Issue':
      return 'security issue';
    case 'Feature Request':
      return 'feature request';
    default:
      return 'support request';
  }
}

export function getDisplayTeamLabel(type: InquiryType | null, routedLabel: string) {
  if (type === 'Feature Request') return 'Product';
  if (routedLabel) return routedLabel;
  return 'Support';
}

export async function postContactSubmission(payload: ContactFormValues & { turnstileToken?: string }) {
  const response = await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: normalizeText(payload.name),
      email: normalizeText(payload.email),
      inquiryType: payload.inquiryType,
      subject: normalizeText(payload.subject),
      message: normalizeText(payload.message),
      companyWebsite: normalizeText(payload.companyWebsite),
      turnstileToken: payload.turnstileToken ? normalizeText(payload.turnstileToken) : '',
    }),
  });

  const body = (await response.json().catch(() => null)) as ContactResponse | null;

  return { response, body };
}